import React, { useEffect, useState } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useAuthStore, useUIStore, useCacheStore } from './store/useStore';
import { apiService } from './services/api';
import Login from './components/Login';
import PhotoGrid from './components/PhotoGrid';
import { Media } from './services/api';

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes (renamed from cacheTime in v5)
    },
  },
});

const App: React.FC = () => {
  const { isAuthenticated, user, token, setAuth, clearAuth } = useAuthStore();
  const { currentAlbum, setCurrentAlbum, isVideoFilterActive, setVideoFilterActive } = useUIStore();
  const { albums, setAlbums, mediaCache, setMediaCache } = useCacheStore();
  
  const [currentMedia, setCurrentMedia] = useState<Media[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [selectedMedia, setSelectedMedia] = useState<Media | null>(null);
  const [isSlideshowActive, setIsSlideshowActive] = useState(false);
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const [viewportHeight, setViewportHeight] = useState(window.innerHeight);
  const [containerWidth, setContainerWidth] = useState(window.innerWidth - 320);
  const [slideshowInterval, setSlideshowInterval] = useState<NodeJS.Timeout | null>(null);
  const [backgroundMusic, setBackgroundMusic] = useState<HTMLAudioElement | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(50);
  const [transitionType, setTransitionType] = useState<'fade' | 'slide' | 'zoom'>('fade');
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [people, setPeople] = useState<any[]>([]);
  const [selectedPerson, setSelectedPerson] = useState<any>(null);
  const [isPeopleFilterActive, setIsPeopleFilterActive] = useState(false);

  // Initialize API token
  useEffect(() => {
    if (token) {
      apiService.setToken(token);
    }
  }, [token]);

  // Keyboard navigation for modal
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (selectedMedia) {
        if (e.key === 'Escape') {
          closeModal();
        } else if (e.key === 'ArrowLeft') {
          previousSlide();
        } else if (e.key === 'ArrowRight') {
          nextSlide();
        }
      }
    };

    document.addEventListener('keydown', handleKeyPress);
    return () => document.removeEventListener('keydown', handleKeyPress);
  }, [selectedMedia, currentSlideIndex, currentMedia]);

  // Track viewport dimensions for dynamic grid calculation
  useEffect(() => {
    const handleResize = () => {
      setViewportHeight(window.innerHeight);
      setContainerWidth(window.innerWidth - 320); // Account for sidebar width
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Cleanup slideshow and music on unmount
  useEffect(() => {
    return () => {
      if (slideshowInterval) {
        clearInterval(slideshowInterval);
      }
      if (backgroundMusic) {
        backgroundMusic.pause();
      }
    };
  }, [slideshowInterval, backgroundMusic]);

  // Load albums and people on authentication
  useEffect(() => {
    if (isAuthenticated && albums.length === 0) {
      loadAlbums();
      loadPeople();
    }
  }, [isAuthenticated, albums.length]);

  // Load media when album changes
  useEffect(() => {
    if (currentAlbum) {
      loadAlbumMedia(currentAlbum.id);
    }
  }, [currentAlbum]);

  const loadAlbums = async () => {
    try {
      setIsLoading(true);
      const response = await apiService.getAlbums();
      setAlbums(response.albums);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load albums');
    } finally {
      setIsLoading(false);
    }
  };

  const loadPeople = async () => {
    try {
      console.log('Loading people...');
      const response = await fetch('/api/people', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('People data received:', data);
      setPeople(data.people || []);
    } catch (err) {
      console.error('Error loading people:', err);
      setError('Failed to load people');
    }
  };

  const loadAlbumMedia = async (albumId: string) => {
    try {
      console.log('Loading media for album:', albumId);
      // Check cache first
      if (mediaCache[albumId]) {
        console.log('Using cached media:', mediaCache[albumId].media.length, 'items');
        setCurrentMedia(mediaCache[albumId].media);
        return;
      }

      setIsLoading(true);
      console.log('Fetching media from API...');
      // Load more items to get all media for pagination
      const response = await apiService.getAlbumMedia(albumId, 1, 1000); // Load up to 1000 items
      console.log('API response:', response);
      console.log('Media count:', response.media.length, 'items');
      console.log('Pagination info:', response.pagination);
      setCurrentMedia(response.media);
      setMediaCache(albumId, response);
    } catch (err) {
      console.error('Error loading media:', err);
      setError(err instanceof Error ? err.message : 'Failed to load media');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAlbumSelect = (album: any) => {
    console.log('Album selected:', album);
    setCurrentAlbum(album);
    setVideoFilterActive(false);
    setCurrentPage(1); // Reset to first page
    // Clear current media to show loading state
    setCurrentMedia([]);
  };

  const handleMediaClick = (media: Media) => {
    console.log('Media clicked:', media);
    setSelectedMedia(media);
    setCurrentSlideIndex(currentMedia.findIndex(m => m.id === media.id));
  };

  const handleSlideshowClick = () => {
    if (currentMedia.length === 0) return;
    
    setIsSlideshowActive(true);
    setCurrentSlideIndex(0);
    setSelectedMedia(currentMedia[0]);
    
    // Start background music
    startBackgroundMusic();
    
    // Start slideshow interval - use all media, not just current page
    const interval = setInterval(() => {
      if (!isTransitioning) {
        setIsTransitioning(true);
        setTransitionType('fade');
        
        setTimeout(() => {
          setCurrentSlideIndex(prev => {
            const nextIndex = (prev + 1) % currentMedia.length;
            setSelectedMedia(currentMedia[nextIndex]);
            return nextIndex;
          });
          
          setTimeout(() => {
            setIsTransitioning(false);
          }, 100);
        }, 300);
      }
    }, 4000); // 4 seconds per slide (increased for transition time)
    
    setSlideshowInterval(interval);
  };

  const startBackgroundMusic = () => {
    // Stop existing music if playing
    if (backgroundMusic) {
      backgroundMusic.pause();
      backgroundMusic.currentTime = 0;
    }
    
    // Select random BGM file (1-120)
    const randomBgmNumber = Math.floor(Math.random() * 120) + 1;
    const bgmPath = `/static/bgms/bgm_${randomBgmNumber}.mp3`;
    
    console.log('Playing random BGM:', bgmPath);
    
    // Create new audio element
    const audio = new Audio(bgmPath);
    audio.loop = true;
    audio.volume = 0.3; // 30% volume
    audio.play().catch(err => {
      console.log('Could not play background music:', err);
      // Try fallback BGM if random one fails
      const fallbackAudio = new Audio('/static/bgms/bgm_1.mp3');
      fallbackAudio.loop = true;
      fallbackAudio.volume = 0.3;
      fallbackAudio.play().catch(fallbackErr => {
        console.log('Fallback BGM also failed:', fallbackErr);
      });
      setBackgroundMusic(fallbackAudio);
    });
    
    setBackgroundMusic(audio);
  };

  const stopBackgroundMusic = () => {
    if (backgroundMusic) {
      backgroundMusic.pause();
      backgroundMusic.currentTime = 0;
      setBackgroundMusic(null);
    }
  };

  const changeBackgroundMusic = () => {
    // Stop current music
    stopBackgroundMusic();
    
    // Start new random music
    setTimeout(() => {
      startBackgroundMusic();
    }, 100); // Small delay to ensure cleanup
  };

  const getTotalPages = () => {
    return Math.ceil(currentMedia.length / itemsPerPage);
  };

  const nextPage = () => {
    const totalPages = getTotalPages();
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const previousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const getCurrentPageMedia = () => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return currentMedia.slice(startIndex, endIndex);
  };

  const togglePeopleFilter = () => {
    setIsPeopleFilterActive(!isPeopleFilterActive);
    if (isPeopleFilterActive) {
      setSelectedPerson(null);
      // Reload all media
      if (currentAlbum) {
        loadAlbumMedia(currentAlbum.id);
      }
    }
  };

  const selectPerson = async (person: any) => {
    setSelectedPerson(person);
    try {
      const response = await fetch(`/api/people/${person.id}/photos`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      setCurrentMedia(data.media || []);
      setCurrentPage(1);
    } catch (err) {
      console.error('Error loading person photos:', err);
    }
  };

  const closeModal = () => {
    setSelectedMedia(null);
    setIsSlideshowActive(false);
    
    // Stop slideshow interval
    if (slideshowInterval) {
      clearInterval(slideshowInterval);
      setSlideshowInterval(null);
    }
    
    // Stop background music
    stopBackgroundMusic();
  };

  const nextSlide = () => {
    if (currentMedia.length === 0 || isTransitioning) return;
    
    setIsTransitioning(true);
    setTransitionType('slide');
    
    setTimeout(() => {
      const newIndex = (currentSlideIndex + 1) % currentMedia.length;
      setCurrentSlideIndex(newIndex);
      setSelectedMedia(currentMedia[newIndex]);
      
      setTimeout(() => {
        setIsTransitioning(false);
      }, 100);
    }, 300);
  };

  const previousSlide = () => {
    if (currentMedia.length === 0 || isTransitioning) return;
    
    setIsTransitioning(true);
    setTransitionType('slide');
    
    setTimeout(() => {
      const newIndex = currentSlideIndex === 0 ? currentMedia.length - 1 : currentSlideIndex - 1;
      setCurrentSlideIndex(newIndex);
      setSelectedMedia(currentMedia[newIndex]);
      
      setTimeout(() => {
        setIsTransitioning(false);
      }, 100);
    }, 300);
  };

  const toggleDarkMode = () => {
    setIsDarkMode(!isDarkMode);
    document.documentElement.setAttribute('data-theme', !isDarkMode ? 'dark' : 'light');
  };

  const handleLogout = () => {
    apiService.clearToken();
    clearAuth();
    setCurrentAlbum(null);
    setCurrentMedia([]);
  };

  const toggleVideoFilter = () => {
    setVideoFilterActive(!isVideoFilterActive);
  };

  if (!isAuthenticated) {
    return <Login />;
  }

  return (
    <div className="main-layout">
      {/* Header */}
      <header className="header">
        <div className="header-left">
          <div className="logo">Photo Gallery</div>
        </div>
        <div className="flex items-center space-x-6">
          <button
            onClick={toggleDarkMode}
            className="text-lg text-gray-600 hover:text-gray-800 dark:text-gray-300 dark:hover:text-white transition-colors"
            title={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
          >
            {isDarkMode ? <i className="fas fa-sun"></i> : <i className="fas fa-moon"></i>}
          </button>
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Welcome, {user?.username}
          </span>
          <button
            onClick={handleLogout}
            className="text-sm text-gray-600 hover:text-gray-800 dark:text-gray-300 dark:hover:text-white transition-colors"
          >
            Logout
          </button>
        </div>
      </header>

      <div className="content-area">
            {/* Sidebar */}
            <div className="sidebar">
              <div className="p-4">
                <h2 className="text-lg font-medium text-gray-900 mb-4">Albums</h2>
                
                {/* Video Filter Toggle */}
                <div className="mb-4">
                  <button
                    onClick={toggleVideoFilter}
                    className={`w-full py-2 px-3 rounded-md text-sm font-medium transition-colors ${
                      isVideoFilterActive
                        ? 'bg-primary-100 text-primary-700 border border-primary-200'
                        : 'bg-gray-100 text-gray-700 border border-gray-200'
                    }`}
                  >
                    {isVideoFilterActive ? 'Show All Media' : 'Show Videos Only'}
                  </button>
                </div>

                {/* People Filter Toggle */}
                <div className="mb-4">
                  <button
                    onClick={togglePeopleFilter}
                    className={`w-full py-2 px-3 rounded-md text-sm font-medium transition-colors ${
                      isPeopleFilterActive
                        ? 'bg-primary-100 text-primary-700 border border-primary-200'
                        : 'bg-gray-100 text-gray-700 border border-gray-200'
                    }`}
                  >
                    {isPeopleFilterActive ? 'Hide People' : `Show People (${people.length})`}
                  </button>
                </div>


                {/* People List */}
                {isPeopleFilterActive && (
                  <div className="mb-4">
                    <h3 className="text-sm font-medium text-gray-700 mb-2">People ({people.length})</h3>
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {people.length > 0 ? (
                        people.map((person) => (
                          <button
                            key={person.id}
                            onClick={() => selectPerson(person)}
                            className={`w-full text-left p-2 rounded-lg transition-colors ${
                              selectedPerson?.id === person.id
                                ? 'bg-primary-50 text-primary-700 border border-primary-200'
                                : 'hover:bg-gray-50 text-gray-700'
                            }`}
                          >
                            <div className="flex items-center space-x-3">
                              <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center">
                                <i className="fas fa-user text-gray-500"></i>
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="text-sm font-medium truncate">{person.display_name}</div>
                                <div className="text-xs text-gray-500">{person.photo_count || 0} photos</div>
                              </div>
                            </div>
                          </button>
                        ))
                      ) : (
                        <div className="text-sm text-gray-500 p-2">
                          {isLoading ? 'Loading people...' : 'No people detected yet'}
                        </div>
                      )}
                    </div>
                  </div>
                )}

            {/* Albums List */}
            <div className="space-y-2">
              {albums.map((album) => (
                <button
                  key={album.id}
                  onClick={() => handleAlbumSelect(album)}
                  className={`w-full text-left p-3 rounded-lg transition-colors ${
                    currentAlbum?.id === album.id
                      ? 'bg-primary-50 text-primary-700 border border-primary-200'
                      : 'hover:bg-gray-50 text-gray-700'
                  }`}
                >
                  <div className="font-medium">{album.name}</div>
                  <div className="text-sm text-gray-500">
                    {album.photo_count} photos
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="main-content">
          {currentAlbum || (isPeopleFilterActive && selectedPerson) ? (
            <>
              {/* Album Header */}
              <div className="bg-white border-b border-gray-200 px-6 py-4 flex-shrink-0">
                <div className="flex justify-between items-center w-full">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-4">
                      <h2 className="text-2xl font-bold text-gray-900">
                        {isPeopleFilterActive && selectedPerson ? selectedPerson.display_name : currentAlbum?.name}
                      </h2>
                      <span className="text-sm text-gray-500">{currentMedia.length} photos</span>
                    </div>
                  </div>
                  
                  {/* Action Buttons */}
                  <div className="flex space-x-2 flex-shrink-0">
                    {/* Pagination */}
                    <div className="flex items-center space-x-2 mr-4">
                      <button
                        onClick={previousPage}
                        disabled={currentPage === 1}
                        className="btn-secondary flex items-center space-x-1 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <i className="fas fa-chevron-left"></i>
                        <span>Prev</span>
                      </button>
                      
                      <span className="text-sm text-gray-600">
                        Page {currentPage} of {getTotalPages()} ({currentMedia.length} total photos)
                      </span>
                      
                      <button
                        onClick={nextPage}
                        disabled={currentPage >= getTotalPages()}
                        className="btn-secondary flex items-center space-x-1 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <span>Next</span>
                        <i className="fas fa-chevron-right"></i>
                      </button>
                    </div>

                    <button
                      onClick={handleSlideshowClick}
                      className="btn-primary flex items-center space-x-2"
                    >
                      <i className="fas fa-play"></i>
                      <span>Slideshow</span>
                    </button>
                    
                        <button
                          onClick={toggleVideoFilter}
                          className={`btn-secondary flex items-center space-x-2 ${
                            isVideoFilterActive ? 'bg-primary-100 text-primary-700' : ''
                          }`}
                        >
                          <i className="fas fa-video"></i>
                          <span>{isVideoFilterActive ? 'All Media' : 'Videos Only'}</span>
                        </button>
                        
                        <button
                          onClick={togglePeopleFilter}
                          className={`btn-secondary flex items-center space-x-2 ${
                            isPeopleFilterActive ? 'bg-primary-100 text-primary-700' : ''
                          }`}
                        >
                          <i className="fas fa-users"></i>
                          <span>{isPeopleFilterActive ? 'All People' : 'People'}</span>
                        </button>
                  </div>
                </div>
              </div>


              {/* Media Container */}
              <div className="media-container">
                {isLoading ? (
                  <div className="flex items-center justify-center h-64">
                    <div className="text-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto mb-4"></div>
                      <span>Loading photos...</span>
                    </div>
                  </div>
                ) : error ? (
                  <div className="text-center text-red-600 p-8">{error}</div>
                ) : (
                  <PhotoGrid
                    media={getCurrentPageMedia()}
                    onMediaClick={handleMediaClick}
                    isVideoFilterActive={isVideoFilterActive}
                    viewportHeight={viewportHeight}
                    containerWidth={containerWidth}
                  />
                )}
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <h3 className="mt-2 text-sm font-medium text-gray-900">
                  {isPeopleFilterActive ? 'No person selected' : 'No album selected'}
                </h3>
                <p className="mt-1 text-sm text-gray-500">
                  {isPeopleFilterActive ? 'Choose a person from the people list to view their photos.' : 'Choose an album from the sidebar to view photos.'}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Photo Modal - Full Screen Slideshow */}
      {selectedMedia && (
        <div className="fixed inset-0 bg-black z-[1001] flex items-center justify-center">
          <div className="relative w-full h-full flex items-center justify-center">
            {/* Close button - more prominent */}
            <button
              onClick={closeModal}
              className="absolute top-4 right-4 text-white text-4xl z-[1002] hover:text-gray-300 transition-colors bg-black bg-opacity-50 rounded-full w-12 h-12 flex items-center justify-center"
              title="Close Slideshow (ESC)"
            >
              <i className="fas fa-times"></i>
            </button>

            {/* Previous button - far left */}
            <button
              onClick={previousSlide}
              className="absolute left-6 top-1/2 transform -translate-y-1/2 text-white text-4xl z-[1002] hover:text-gray-300 transition-colors bg-black bg-opacity-50 rounded-full w-16 h-16 flex items-center justify-center"
            >
              <i className="fas fa-chevron-left"></i>
            </button>

            {/* Next button - far right */}
            <button
              onClick={nextSlide}
              className="absolute right-6 top-1/2 transform -translate-y-1/2 text-white text-4xl z-[1002] hover:text-gray-300 transition-colors bg-black bg-opacity-50 rounded-full w-16 h-16 flex items-center justify-center"
            >
              <i className="fas fa-chevron-right"></i>
            </button>

            {/* Slideshow controls */}
            {isSlideshowActive && (
              <div className="absolute top-6 left-1/2 transform -translate-x-1/2 z-[1002] flex space-x-2">
                <button
                  onClick={() => {
                    if (slideshowInterval) {
                      clearInterval(slideshowInterval);
                      setSlideshowInterval(null);
                    } else {
                      const interval = setInterval(() => {
                        setCurrentSlideIndex(prev => {
                          const nextIndex = (prev + 1) % currentMedia.length;
                          setSelectedMedia(currentMedia[nextIndex]);
                          return nextIndex;
                        });
                      }, 3000);
                      setSlideshowInterval(interval);
                    }
                  }}
                  className="bg-black bg-opacity-50 text-white px-4 py-2 rounded-lg hover:bg-opacity-70 transition-colors"
                >
                  {slideshowInterval ? (
                    <><i className="fas fa-pause mr-2"></i>Pause</>
                  ) : (
                    <><i className="fas fa-play mr-2"></i>Resume</>
                  )}
                </button>
                
                <button
                  onClick={changeBackgroundMusic}
                  className="bg-black bg-opacity-50 text-white px-4 py-2 rounded-lg hover:bg-opacity-70 transition-colors"
                  title="Change Background Music"
                >
                  <i className="fas fa-music mr-2"></i>New Song
                </button>
                
                <select
                  value={transitionType}
                  onChange={(e) => setTransitionType(e.target.value as 'fade' | 'slide' | 'zoom')}
                  className="bg-black bg-opacity-50 text-white px-3 py-2 rounded-lg hover:bg-opacity-70 transition-colors border border-gray-600"
                  title="Transition Effect"
                >
                  <option value="fade">Fade</option>
                  <option value="slide">Slide</option>
                  <option value="zoom">Zoom</option>
                </select>
              </div>
            )}

            {/* Media content - full height */}
            <div className="w-full h-full flex flex-col items-center justify-center p-8 slideshow-container">
              {selectedMedia.file_type === 'video' ? (
                <video
                  src={selectedMedia.url}
                  controls
                  className={`max-w-full max-h-full object-contain slideshow-image slideshow-${transitionType}-enter${isTransitioning ? '-active' : ''}`}
                  autoPlay
                />
              ) : (
                <img
                  src={selectedMedia.url}
                  alt={selectedMedia.filename}
                  className={`max-w-full max-h-full object-contain slideshow-image slideshow-${transitionType}-enter${isTransitioning ? '-active' : ''}`}
                />
              )}
              
              {/* Media info - positioned at bottom */}
              <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 text-white text-center">
                <h3 className="text-xl font-medium mb-2">{selectedMedia.filename}</h3>
                <p className="text-sm text-gray-300">
                  {currentSlideIndex + 1} of {currentMedia.length}
                  {isSlideshowActive && (
                    <span className="ml-2 text-yellow-300">
                      <i className="fas fa-music mr-1"></i>Slideshow
                    </span>
                  )}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const AppWithProviders: React.FC = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <App />
    </QueryClientProvider>
  );
};

export default AppWithProviders;